import { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/DatePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusChip } from "@/components/StatusChip";
import { toast } from "sonner";
import { Search } from "lucide-react";

export default function SwapHistory() {
  const [swaps, setSwaps] = useState([]);
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [photo, setPhoto] = useState(null);

  const load = () => {
    const params = {};
    if (search) params.search = search;
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    api.get("/swaps", { params }).then((r) => setSwaps(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search, from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-5" data-testid="swap-history-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Swap History</h1>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input data-testid="swap-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search rider name or swap ID" className="pl-9 bg-card border-border" />
        </div>
        <DatePicker value={from} onChange={setFrom} placeholder="From date" testid="swap-date-from" />
        <DatePicker value={to} onChange={setTo} placeholder="To date" testid="swap-date-to" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {swaps.map((s) => (
          <div key={s.swap_id} className="card-tactical p-4 space-y-3" data-testid={`swap-card-${s.swap_id}`}>
            <div className="flex items-center justify-between">
              <div>
                <span className="font-mono font-semibold">{s.swap_id}</span>
                <span className="text-slate-500 text-xs ml-2">{fmtDateTime(s.timestamp)}</span>
              </div>
              {s.old_battery_faulty && <StatusChip label="Old battery faulty" tone="overdue" testid={`swap-faulty-${s.swap_id}`} />}
            </div>
            <div className="text-sm">
              <span className="font-medium">{s.rider_name}</span>
              <span className="text-slate-500 font-mono text-xs ml-2">{s.rider_id}</span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-slate-500 uppercase tracking-wider mb-1">Inward (returned)</div>
                <div className="font-mono">{s.old_battery_serial}</div>
                {s.inward_photo && (
                  <img src={s.inward_photo} alt="inward battery" data-testid={`inward-photo-${s.swap_id}`}
                    className="mt-1.5 h-24 w-full object-cover rounded-lg border border-border cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setPhoto({ src: s.inward_photo, title: `${s.swap_id} — Inward` })} />
                )}
              </div>
              <div>
                <div className="text-slate-500 uppercase tracking-wider mb-1">Outward (issued)</div>
                <div className="font-mono">{s.new_battery_serial}</div>
                {s.outward_photo && (
                  <img src={s.outward_photo} alt="outward battery" data-testid={`outward-photo-${s.swap_id}`}
                    className="mt-1.5 h-24 w-full object-cover rounded-lg border border-border cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setPhoto({ src: s.outward_photo, title: `${s.swap_id} — Outward` })} />
                )}
              </div>
            </div>
            <div className="text-xs text-slate-400">Odometer: <span className="font-mono text-slate-700">{s.odometer_km} km</span></div>
          </div>
        ))}
        {swaps.length === 0 && (
          <div className="card-tactical p-8 text-center text-slate-500 lg:col-span-2" data-testid="swaps-empty">No swap records found.</div>
        )}
      </div>

      <Dialog open={!!photo} onOpenChange={() => setPhoto(null)}>
        <DialogContent className="bg-card border-border sm:max-w-2xl" data-testid="photo-viewer-modal">
          <DialogHeader><DialogTitle className="font-heading">{photo?.title}</DialogTitle></DialogHeader>
          {photo && <img src={photo.src} alt={photo.title} className="w-full rounded-lg" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
